/**
 * reportScoringPolicy — WHERE a report type's authoritative headline score
 * comes from.
 *
 * Historically this module answered a single boolean ("does this type score?"),
 * which conflated two different things: whether a type has a deterministic
 * CATEGORY composite, and whether it has an authoritative score at all. Public
 * Sentiment broke that conflation — it has no category composite, but it does
 * emit a real 0-100 SENTIMENT INDEX. Under the boolean model its verdict tier
 * resolved to 'none' forever, which meant it could never floor the Go/No-Go
 * rollup (`executiveDecisionRollup` PATH 2 skips `tier === 'none'` rows).
 *
 * The basis model names the source instead:
 *
 *   - 'category-composite' — `report.overallScore`, the weighted category mean
 *     written by `siteRagService.scoreSite*`. The default.
 *   - 'sentiment-index'    — `report.sentimentIndex.score`, the PSR-2 0-100
 *     index (higher = more supportive). `report.overallScore` on these reports
 *     is the `: 50` PLACEHOLDER siteRagService falls back to when `categories`
 *     is undefined — reading it would band every PSR "Mixed" regardless of what
 *     the report actually found. It must never be used.
 *   - 'none'               — no authoritative score. Render "—", never "0",
 *     never "/100". These types carry the same `: 50` placeholder.
 *
 * `reportTypeUsesScoring` is retained with its original meaning ("is there any
 * authoritative score") so existing call sites are unchanged; note that it now
 * answers TRUE for public-sentiment.
 */
import type { ReportTypeId } from '../config/reportTypes';
import type { SiteRagReport } from '../types/siteRag';

/** Where a report type's authoritative headline score comes from. */
export type ReportScoreBasis = 'category-composite' | 'sentiment-index' | 'none';

/**
 * Report types with no authoritative headline score of any kind.
 *
 * NB: this is NOT the same as `ReportPromptConfig.usesScoring === false`.
 * `financial` and `public-sentiment` both set `usesScoring: false` (they skip
 * the GENERIC category scorer) yet both produce an authoritative score by
 * another route — `scoreSiteFinancial` and the sentiment index respectively.
 * The prompt-config flag gates the generic scorer; this list gates the verdict.
 */
export const NO_SCORING_REPORT_TYPES: readonly ReportTypeId[] = [
  'test-fit',
  'custom-brief',
  'executive-summary',
] as const;

/** Report types whose authoritative score is the PSR-2 sentiment index. */
const SENTIMENT_INDEX_REPORT_TYPES: readonly ReportTypeId[] = [
  'public-sentiment',
] as const;

/** Resolve the score basis for a report type. */
export function reportScoreBasis(reportType: ReportTypeId | undefined): ReportScoreBasis {
  if (!reportType) return 'category-composite';
  if (NO_SCORING_REPORT_TYPES.includes(reportType)) return 'none';
  if (SENTIMENT_INDEX_REPORT_TYPES.includes(reportType)) return 'sentiment-index';
  return 'category-composite';
}

/** Does this report type carry an authoritative headline score at all? */
export function reportTypeUsesScoring(reportType: ReportTypeId | undefined): boolean {
  return reportScoreBasis(reportType) !== 'none';
}

/**
 * The PSR-2 sentiment index as a 0-100 score, or null when absent/unusable.
 *
 * `sentimentIndex` is NOT in the PUBLIC_SENTIMENT_SCHEMA `required` array — a
 * PSR-1 scaffold report may legitimately omit it. Absent ⇒ null ⇒ tier 'none'
 * (neutral in the rollup), never a fabricated downgrade. 0 is a LEGITIMATE
 * value here (maximally opposed), unlike the category composite where 0 is the
 * uninitialised sentinel — so this resolver deliberately does not reject it.
 */
export function resolveSentimentIndexScore(report: SiteRagReport): number | null {
  const raw = report.sentimentIndex?.score;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  return Math.max(0, Math.min(100, raw));
}
